import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, HeartPulse, Users, PieChart, Clock, LayoutList } from 'lucide-react';
import { Toolbar } from '@/shared/components/custom/toolbar/Toolbar';
import { KpiCardsSection } from '../components/KpiCardsSection/KpiCardsSection';
import { TaskStatusOverviewSection } from '../components/TaskStatusOverviewSection/TaskStatusOverviewSection';
import { CollapsibleSection } from '../components/CollapsibleSection/CollapsibleSection';
import { OverdueTasksTable } from '../components/OverdueTasksTable/OverdueTasksTable';
import { ProjectHealthList } from '../components/ProjectHealthList/ProjectHealthList';
import { TeamWorkloadList } from '../components/TeamWorkloadList/TeamWorkloadList';
import { TaskDistributionSection } from '../components/TaskDistributionSection/TaskDistributionSection';
import { TimeAnalysisSection } from '../components/TimeAnalysisSection/TimeAnalysisSection';
import { AllTasksTable } from '../components/AllTasksTable/AllTasksTable';
import { useOrgSummary } from '../hooks/useOrgSummary';
import { useTaskStatusOverview } from '../hooks/useTaskStatusOverview';

export default function OrgSummaryPage() {
  const { t } = useTranslation('analytics');

  const { data: orgSummary, isLoading: isOrgSummaryLoading } = useOrgSummary();
  const { data: taskStatus, isLoading: isTaskStatusLoading } = useTaskStatusOverview();

  const [openSections, setOpenSections] = useState({
    overdueTasks: true,
    projectHealth: true,
    teamWorkload: true,
    taskDistribution: true,
    timeAnalysis: true,
    allTasks: true,
  });

  const toggle = (key: keyof typeof openSections) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="analytics-page">
      <Toolbar>
        <Toolbar.Info title={t('page.title')} description={t('page.subtitle')} />
      </Toolbar>

      <KpiCardsSection
        orgSummary={orgSummary}
        taskStatus={taskStatus}
        isLoading={isOrgSummaryLoading || isTaskStatusLoading}
      />

      <TaskStatusOverviewSection data={taskStatus} isLoading={isTaskStatusLoading} />

      <CollapsibleSection
        title={t('sections.overdueTasks')}
        count={orgSummary?.overdueTasks}
        isOpen={openSections.overdueTasks}
        onToggle={() => toggle('overdueTasks')}
        icon={AlertTriangle}
        iconClassName="text-destructive"
        data-testid="analytics-section-overdue-tasks"
      >
        <OverdueTasksTable />
      </CollapsibleSection>

      <CollapsibleSection
        title={t('sections.projectHealth')}
        isOpen={openSections.projectHealth}
        onToggle={() => toggle('projectHealth')}
        icon={HeartPulse}
        iconClassName="text-emerald-500"
        data-testid="analytics-section-project-health"
      >
        <ProjectHealthList />
      </CollapsibleSection>

      <CollapsibleSection
        title={t('sections.teamWorkload')}
        isOpen={openSections.teamWorkload}
        onToggle={() => toggle('teamWorkload')}
        icon={Users}
        iconClassName="text-blue-500"
        data-testid="analytics-section-team-workload"
      >
        <TeamWorkloadList />
      </CollapsibleSection>

      <CollapsibleSection
        title={t('sections.taskDistribution')}
        isOpen={openSections.taskDistribution}
        onToggle={() => toggle('taskDistribution')}
        icon={PieChart}
        iconClassName="text-violet-500"
        data-testid="analytics-section-task-distribution"
      >
        <TaskDistributionSection />
      </CollapsibleSection>

      <CollapsibleSection
        title={t('sections.timeAnalysis')}
        isOpen={openSections.timeAnalysis}
        onToggle={() => toggle('timeAnalysis')}
        icon={Clock}
        iconClassName="text-amber-500"
        data-testid="analytics-section-time-analysis"
      >
        <TimeAnalysisSection />
      </CollapsibleSection>

      <CollapsibleSection
        title={t('sections.allTasks')}
        count={orgSummary?.totalTasks}
        isOpen={openSections.allTasks}
        onToggle={() => toggle('allTasks')}
        icon={LayoutList}
        data-testid="analytics-section-all-tasks"
      >
        <AllTasksTable />
      </CollapsibleSection>
    </div>
  );
}
